/**
 * Protocolo janela real ↔ iframe do tutorial (spec 6.1-7).
 *
 * Regra de seguranca (`security-baseline.md`): todo `postMessage` leva
 * `targetOrigin` explicito — a propria origem, ja que o iframe e' da mesma
 * origem — e todo listener valida `event.origin` E `event.source` antes de
 * olhar o payload. Mensagem de outra origem, ou de outro iframe da mesma
 * origem, e' ignorada.
 */

export const TUTORIAL_MESSAGE_SOURCE = 'oprus-crm-tutorial';

export type TutorialMessage =
  | { readonly type: 'tutorial:ready'; readonly total: number; readonly title: string }
  | { readonly type: 'tutorial:step'; readonly index: number; readonly total: number }
  | { readonly type: 'tutorial:finished' }
  | { readonly type: 'tutorial:close' }
  | { readonly type: 'tutorial:error'; readonly message: string };

type Payload = Record<string, unknown>;

function isPayload(value: unknown): value is Payload {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCount(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

function toMessage(data: Payload): TutorialMessage | null {
  switch (data.type) {
    case 'tutorial:ready':
      return isCount(data.total) && typeof data.title === 'string'
        ? { type: data.type, total: data.total, title: data.title }
        : null;
    case 'tutorial:step':
      return isCount(data.index) && isCount(data.total)
        ? { type: data.type, index: data.index, total: data.total }
        : null;
    case 'tutorial:error':
      return typeof data.message === 'string' ? { type: data.type, message: data.message } : null;
    case 'tutorial:finished':
    case 'tutorial:close':
      return { type: data.type };
    default:
      return null;
  }
}

/**
 * Le uma mensagem do protocolo. `expectedSource` e' a janela de quem pode
 * falar: o `contentWindow` do iframe (lado da janela real) ou `window.parent`
 * (lado do iframe). Qualquer divergencia devolve `null`.
 */
export function parseTutorialMessage(
  event: MessageEvent,
  expectedSource: MessageEventSource | Window | null,
): TutorialMessage | null {
  if (event.origin !== window.location.origin) return null;
  if (!expectedSource || event.source !== expectedSource) return null;
  if (!isPayload(event.data) || event.data.source !== TUTORIAL_MESSAGE_SOURCE) return null;
  return toMessage(event.data);
}

export function postTutorialMessage(target: Window, message: TutorialMessage): void {
  target.postMessage({ source: TUTORIAL_MESSAGE_SOURCE, ...message }, window.location.origin);
}
